import { useEffect, useState } from "react";

import { AUTOSAVE_OPTIONS, getAdminSettings, setAdminSettings } from "@/lib/admin-settings";

export function AdminSettings() {
  const [autosaveInterval, setAutosaveInterval] = useState<number>(
    getAdminSettings().autosaveInterval
  );
  const [saved, setSaved] = useState(false);

  // Load settings on mount
  useEffect(() => {
    const settings = getAdminSettings();
    setAutosaveInterval(settings.autosaveInterval);
  }, []);

  const handleIntervalChange = (value: number) => {
    setAutosaveInterval(value);
    setAdminSettings({ autosaveInterval: value });
    setSaved(true);

    // Hide the saved message after 2 seconds
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div>
      <h1 className="text-[24px] md:text-[28px] font-bold text-primary mb-6 md:mb-8">Settings</h1>

      <div className="max-w-xl">
        {/* Autosave Settings */}
        <div className="border border-border bg-card p-4 md:p-6 mb-6">
          <h2 className="text-[11px] text-text-muted font-bold tracking-caps uppercase mb-4">
            Autosave
          </h2>

          <div className="mb-4">
            <label className="block text-[13px] text-text-secondary mb-2">Autosave Interval</label>
            <div className="relative">
              <select
                value={autosaveInterval}
                onChange={(e) => handleIntervalChange(Number(e.target.value))}
                className="w-full px-3 py-2.5 bg-background border border-border text-text-primary text-[13px] focus:outline-none focus:border-border-hover appearance-none cursor-pointer"
              >
                {AUTOSAVE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg
                  className="size-4 text-text-primary"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M6 9l6 6 6-6" strokeLinecap="square" strokeLinejoin="miter" />
                </svg>
              </div>
            </div>
            <p className="text-[11px] text-text-faint mt-2">
              How often posts and projects are automatically saved while editing.
              {autosaveInterval === 0 && (
                <span className="text-orange-500 ml-1">
                  Autosave is disabled. Remember to save manually!
                </span>
              )}
            </p>
          </div>

          {saved && (
            <div className="flex items-center gap-2 text-[12px] text-green-500">
              <div className="size-2 bg-green-500" />
              Settings saved
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="border border-border bg-card p-4 md:p-6">
          <h2 className="text-[11px] text-text-muted font-bold tracking-caps uppercase mb-4">
            About Autosave
          </h2>
          <div className="space-y-3 text-[13px] text-text-tertiary">
            <p>
              Autosave automatically saves your work at regular intervals while you're editing posts
              or projects. This helps prevent data loss if you forget to save or if something
              unexpected happens.
            </p>
            <p>
              <strong className="text-text-secondary">Status indicators:</strong>
            </p>
            <ul className="space-y-2 ml-4">
              <li className="flex items-center gap-2">
                <div className="size-2 bg-green-500" />
                <span>Saved - Your changes have been saved</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="size-2 bg-orange-500" />
                <span>Unsaved changes - Changes pending save</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="size-2 bg-yellow-500" />
                <span>Saving - Save in progress</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
